import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { getToken } from "../services/api";

const S = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 },
  input: { background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 8, color: "#111827", padding: "8px 12px", fontSize: 13, width: "100%" },
  btn: { padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  kpi: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", flex: 1 },
};

const FORMAT_COLORS = { "Factur-X": "#1a9e72", "UBL 2.1": "#185fa5", "CII": "#8b5cf6", "PDF/A": "#f59e0b" };
const STATUS_COLORS = { sent: "#1a9e72", pending: "#f59e0b", error: "#ef4444", received: "#185fa5", accepted: "#22c55e", rejected: "#ef4444", "envoyée": "#1a9e72", "erreur": "#ef4444", "brouillon": "#4b5563" };
const STATUS_LABELS = { sent: "Envoyée", pending: "En attente", error: "Erreur", received: "Reçue", accepted: "Acceptée", rejected: "Rejetée", "envoyée": "Envoyée", "erreur": "Erreur", "brouillon": "Brouillon" };

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

// Static demo data for e-invoices
const DEMO_INVOICES = [
  { id: 1, number: "FA-2026-0142", client: "Orange Business France", amount: 12840, format: "Factur-X", status: "sent", channel: "Chorus Pro", date: "2026-06-08", piste_audit: true },
  { id: 2, number: "FA-2026-0141", client: "Ministère de l'Économie", amount: 5200, format: "UBL 2.1", status: "accepted", channel: "Chorus Pro", date: "2026-06-05", piste_audit: true },
  { id: 3, number: "FA-2026-0140", client: "SNCF Réseau", amount: 8750, format: "Factur-X", status: "pending", channel: "Chorus Pro", date: "2026-06-01", piste_audit: true },
  { id: 4, number: "FA-2026-0139", client: "TechStart SAS", amount: 3200, format: "PDF/A", status: "sent", channel: "Email + PDF", date: "2026-05-28", piste_audit: false },
  { id: 5, number: "FA-2026-0138", client: "Airbus Operations", amount: 24500, format: "UBL 2.1", status: "accepted", channel: "EDI direct", date: "2026-05-22", piste_audit: true },
  { id: 6, number: "FA-2026-0137", client: "Collectivité Paris 14", amount: 1850, format: "Factur-X", status: "error", channel: "Chorus Pro", date: "2026-05-20", piste_audit: true },
];

export default function FactElec({ currentCompanyId, currentSociete, onNavigate }) {
  const {} = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);

  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const token = await getToken();
      const resp = await fetch('/api/factelec/invoices', {
        headers: { Authorization: 'Bearer ' + token }
      });
      const json = await resp.json();
      if (resp.ok && Array.isArray(json.data)) {
        const mapped = json.data.map(e => ({
          id: e.einvoice_id,
          invoice_id: e.invoice_id,
          number: e.number || ('#' + e.invoice_id),
          client: e.client_name || '—',
          amount: e.ttc_amount || 0,
          format: e.format === 'UBL' ? 'UBL 2.1' : (e.format || '—'),
          status: e.status,
          date: e.date || (e.created_at ? e.created_at.slice(0, 10) : ''),
          piste_audit: e.status === 'envoyée',
          superpdp_doc_id: e.superpdp_doc_id,
          error_message: e.error_message,
        }));
        setInvoices(mapped);
      }
    } catch (err) {
      console.error('ERREUR_FETCH_INVOICES:', err);
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterFormat, setFilterFormat] = useState("");
  const [toast, setToast] = useState(null);
  const [pdpStatus, setPdpStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const fetchPdpStatus = useCallback(async () => {
    if (!currentCompanyId) return;
    setStatusLoading(true);
    try {
      const token = await getToken();
      const resp = await fetch('/api/factelec/status?company_id=' + currentCompanyId, {
        headers: { Authorization: 'Bearer ' + token }
      });
      const data = await resp.json();
      if (resp.ok) setPdpStatus(data);
    } catch (err) {
      console.error('ERREUR_FETCH_PDP_STATUS:', err);
    } finally {
      setStatusLoading(false);
    }
  }, [currentCompanyId]);

  useEffect(() => { fetchPdpStatus(); }, [fetchPdpStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('factelec_connecte') === '1') {
      setToast({ msg: 'Connexion à SUPER PDP réussie !', type: 'success' });
      setTimeout(() => setToast(null), 3500);
      setTab('config');
      fetchPdpStatus();
      params.delete('factelec_connecte');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const handleConnectSuperPDP = async () => {
    try {
      const token = await getToken();
      const resp = await fetch('/api/factelec/oauth/authorize?company_id=' + currentCompanyId, {
        headers: { Authorization: 'Bearer ' + token }
      });
      const data = await resp.json();
      if (!resp.ok || !data.redirect_url) {
        showToast(data.error || 'Erreur de connexion a SUPER PDP', 'error');
        return;
      }
      window.location.href = data.redirect_url;
    } catch (err) {
      console.error('ERREUR_HANDLECONNECT:', err);
      showToast('Erreur de connexion a SUPER PDP', 'error');
    }
  };

  const kpis = {
    total: invoices.length,
    sent: invoices.filter(i => i.status === "sent" || i.status === "accepted").length,
    pending: invoices.filter(i => i.status === "pending").length,
    errors: invoices.filter(i => i.status === "error").length,
    totalAmount: invoices.reduce((s, i) => s + i.amount, 0),
    paFComplient: invoices.filter(i => i.piste_audit).length,
  };

  const filtered = invoices.filter(i => {
    if (filterStatus && i.status !== filterStatus) return false;
    if (filterFormat && i.format !== filterFormat) return false;
    return true;
  });

  const TABS = [
    { id: "dashboard", label: "📊 Tableau de bord" },
    { id: "invoices",  label: "📄 Factures envoyées" },
    { id: "config",    label: "⚙️ Configuration" },
    { id: "paf",       label: "🔍 Piste d'audit" },
  ];

  return (
    <div style={{ padding: "0 0 40px" }}>
      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "12px 20px", borderRadius: 10, background: toast.type === "error" ? "#ef4444" : "#1a9e72", color: "#fff", fontWeight: 700, fontSize: 13 }}>{toast.msg}</div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#111827" }}>✅ Facturation électronique</h2>
          <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>{currentSociete} — Conformité réforme e-invoicing 2026</div>
        </div>
        <button onClick={() => onNavigate?.("facturation")} style={{ ...S.btn, background: "#1a9e72", color: "#fff" }}>+ Créer une facture électronique</button>
      </div>

      {/* Conformity banner */}
      <div style={{ background: "rgba(26,158,114,0.1)", border: "1px solid rgba(26,158,114,0.3)", borderRadius: 12, padding: "14px 20px", marginBottom: 24, display: "flex", gap: 16, alignItems: "center" }}>
        <span style={{ fontSize: 28 }}>✅</span>
        <div style={{ flex: 1 }}>
          <div style={{ color: "#1a9e72", fontWeight: 800, fontSize: 14 }}>Plateforme Agréée (PA) SUPER PDP — Certifiée DGFiP</div>
          <div style={{ color: "#4b5563", fontSize: 13 }}>ExecPro360 s'appuie sur SUPER PDP, Plateforme Agréée immatriculée définitivement par la DGFiP depuis décembre 2025, certifiée ISO 27001. Conformité totale à la réforme obligatoire dès septembre 2026. Formats supportés : Factur-X, UBL 2.1, CII.</div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: "#4b5563" }}>Prochain jalon</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#f59e0b" }}>01/09/2026</div>
          <div style={{ fontSize: 11, color: "#4b5563" }}>Grandes entreprises obligatoires</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #e5e7eb" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ ...S.btn, background: "transparent", color: tab === t.id ? "#1a9e72" : "#4b5563", borderBottom: `2px solid ${tab === t.id ? "#1a9e72" : "transparent"}`, borderRadius: 0, padding: "8px 14px 10px", fontSize: 12 }}>{t.label}</button>
        ))}
      </div>

      {/* ── Dashboard ── */}
      {tab === "dashboard" && (
        <div>
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            {[
              { label: "Total factures e-invoicing", value: String(kpis.total), color: "#111827" },
              { label: "Envoyées / Acceptées", value: String(kpis.sent), color: "#1a9e72" },
              { label: "En attente réponse", value: String(kpis.pending), color: "#f59e0b" },
              { label: "Erreurs", value: String(kpis.errors), color: kpis.errors > 0 ? "#ef4444" : "#4b5563" },
              { label: "Montant total", value: fmt(kpis.totalAmount) + " €", color: "#185fa5" },
            ].map(k => (
              <div key={k.label} style={{ ...S.kpi, minWidth: 140 }}>
                <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 6 }}>{k.label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Formats breakdown */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 20, marginBottom: 20, maxWidth: 500 }}>
            <div style={S.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Formats utilisés</div>
              {["Factur-X", "UBL 2.1", "CII", "PDF/A"].map(f => {
                const count = invoices.filter(i => i.format === f).length;
                const pct = invoices.length > 0 ? Math.round((count / invoices.length) * 100) : 0;
                return (
                  <div key={f} style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: "#111827" }}>{f}</span>
                      <span style={{ color: FORMAT_COLORS[f], fontWeight: 700 }}>{count} ({pct}%)</span>
                    </div>
                    <div style={{ height: 6, background: "#f9fafb", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: pct + "%", background: FORMAT_COLORS[f], borderRadius: 3 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Errors to fix */}
          {kpis.errors > 0 && (
            <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ color: "#ef4444", fontWeight: 700, marginBottom: 8 }}>⚠️ {kpis.errors} facture(s) en erreur — action requise</div>
              {invoices.filter(i => i.status === "error").map(inv => (
                <div key={inv.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(239,68,68,0.15)" }}>
                  <span style={{ color: "#111827", fontSize: 13 }}>{inv.number} — {inv.client}</span>
                  <button onClick={() => onNavigate?.("facturation")} style={{ ...S.btn, padding: "3px 10px", background: "rgba(239,68,68,0.2)", color: "#ef4444", fontSize: 12 }}>Voir la facture</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Factures ── */}
      {tab === "invoices" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...S.input, width: 150 }}>
              <option value="">Tous statuts</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={filterFormat} onChange={e => setFilterFormat(e.target.value)} style={{ ...S.input, width: 140 }}>
              <option value="">Tous formats</option>
              {["Factur-X", "UBL 2.1", "CII", "PDF/A"].map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>

          <div style={S.card}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  {["N° Facture", "Client", "Montant TTC", "Format", "Statut", "Date", "PAF", "Actions"].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "#4b5563", fontWeight: 600, fontSize: 10, textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(inv => {
                  const sc = STATUS_COLORS[inv.status] || "#4b5563";
                  const fc = FORMAT_COLORS[inv.format] || "#4b5563";
                  return (
                    <tr key={inv.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "8px 12px", color: "#185fa5", fontWeight: 700 }}>{inv.number}</td>
                      <td style={{ padding: "8px 12px", color: "#111827" }}>{inv.client}</td>
                      <td style={{ padding: "8px 12px", color: "#1a9e72", fontWeight: 700 }}>{fmt(inv.amount)} €</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 20, background: fc + "22", color: fc, fontWeight: 700 }}>{inv.format}</span>
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 20, background: sc + "22", color: sc, fontWeight: 700 }}>{STATUS_LABELS[inv.status] || inv.status}</span>
                      </td>
                      <td style={{ padding: "8px 12px", color: "#4b5563", fontSize: 12 }}>{inv.date}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ fontSize: 14 }}>{inv.piste_audit ? "✅" : "❌"}</span>
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <button onClick={async () => {
                          try {
                            const blob = await api.documents.invoicePdf(inv.invoice_id, 'fr', 'facture');
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank', 'noopener,noreferrer');
                          } catch (err) {
                            showToast(err.message || 'Impossible de charger le PDF.', 'error');
                          }
                        }} style={{ ...S.btn, padding: "3px 8px", background: "rgba(24,95,165,0.2)", color: "#60a5fa", fontSize: 11 }}>⬇️</button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#4b5563" }}>Aucune facture</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Chorus Pro ── */}
      {tab === "config" && (
        <div>
          <div style={{ ...S.card, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Connexion SUPER PDP</div>
            <div style={{ fontSize: 12, color: "#4b5563", marginTop: 4, marginBottom: 16 }}>Autorisez ExecPro360 à accéder à votre compte SUPER PDP pour émettre et recevoir vos factures électroniques.</div>
            {statusLoading ? (
              <div style={{ fontSize: 13, color: "#4b5563" }}>Chargement du statut…</div>
            ) : !pdpStatus || !pdpStatus.oauth_authorized ? (
              <button onClick={handleConnectSuperPDP} style={{ ...S.btn, background: "#1a9e72", color: "#fff" }}>Connecter SUPER PDP</button>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { done: pdpStatus.oauth_authorized, label: "Compte SUPER PDP connecté" },
                  { done: pdpStatus.kyc_validated, label: "Vérification d'identité (KYC) validée" },
                  { done: pdpStatus.directory_registered, label: "Inscription à l'annuaire des entreprises" },
                ].map((step, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                    <span style={{ color: step.done ? "#1a9e72" : "#f59e0b", fontWeight: 700 }}>{step.done ? "✓" : "○"}</span>
                    <span style={{ color: step.done ? "#111827" : "#4b5563" }}>{step.label}</span>
                  </div>
                ))}
                {pdpStatus.status && (
                  <div style={{ fontSize: 11, color: "#4b5563", marginTop: 4 }}>Statut interne : {pdpStatus.status}</div>
                )}
                <button onClick={handleConnectSuperPDP} style={{ ...S.btn, alignSelf: "flex-start", marginTop: 4, background: "transparent", border: "1px solid #d1d5db", color: "#4b5563" }}>Se reconnecter</button>
              </div>
            )}
          </div>
          <div style={{ ...S.card, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Format par défaut</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { f: "Factur-X", desc: "Standard franco-allemand — PDF hybride avec XML embarqué. Recommandé pour B2B.", icon: "🇫🇷" },
                { f: "UBL 2.1", desc: "Format XML universel — recommandé pour les marchés publics et clients internationaux.", icon: "🌍" },
                { f: "CII", desc: "Cross-Industry Invoice — format ONU. Compatible avec tous les acteurs européens.", icon: "🇪🇺" },
                { f: "PDF/A", desc: "PDF archivable avec signature électronique. Transitoire jusqu'à 2026.", icon: "📄" },
              ].map(item => (
                <div key={item.f} style={{ padding: 14, background: "#f9fafb", borderRadius: 10, border: "1px solid #e5e7eb" }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 18 }}>{item.icon}</span>
                    <div style={{ color: FORMAT_COLORS[item.f], fontWeight: 700, fontSize: 13 }}>{item.f}</div>
                  </div>
                  <div style={{ color: "#4b5563", fontSize: 12 }}>{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Options d'archivage</div>
            {[
              { label: "Archivage automatique (10 ans légaux)", active: true },
              { label: "Signature électronique qualifiée (eIDAS)", active: true },
              { label: "Horodatage des émissions", active: true },
              { label: "Stockage chiffré (AES-256)", active: true },
              { label: "Export FEC annuel automatique", active: false },
            ].map(opt => (
              <div key={opt.label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                <span style={{ color: "#111827", fontSize: 13 }}>{opt.label}</span>
                <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 700, background: opt.active ? "rgba(26,158,114,0.2)" : "rgba(100,116,139,0.2)", color: opt.active ? "#1a9e72" : "#4b5563" }}>
                  {opt.active ? "Actif" : "Inactif"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Piste d'audit ── */}
      {tab === "paf" && (
        <div>
          <div style={{ background: "rgba(24,95,165,0.1)", border: "1px solid rgba(24,95,165,0.3)", borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 13, color: "#60a5fa" }}>
            ℹ️ La Piste d'Audit Fiable (PAF) garantit la traçabilité entre les commandes, bons de livraison et factures. Elle est obligatoire pour justifier la déduction de TVA (Article 289-VII du CGI).
          </div>

          <div style={{ ...S.card, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Conformité PAF</div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1, padding: 16, background: "rgba(26,158,114,0.1)", borderRadius: 10, border: "1px solid rgba(26,158,114,0.3)", textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: "#1a9e72" }}>{kpis.paFComplient}</div>
                <div style={{ fontSize: 12, color: "#4b5563" }}>Factures avec PAF complète</div>
              </div>
              <div style={{ flex: 1, padding: 16, background: "rgba(245,158,11,0.1)", borderRadius: 10, border: "1px solid rgba(245,158,11,0.3)", textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: "#f59e0b" }}>{invoices.length - kpis.paFComplient}</div>
                <div style={{ fontSize: 12, color: "#4b5563" }}>Factures sans PAF (PDF simple)</div>
              </div>
            </div>
          </div>

          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Chaîne documentaire</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                {["Facture", "Bon de commande", "Bon de livraison", "PAF"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "#4b5563", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "8px 12px", color: "#185fa5", fontWeight: 700 }}>{inv.number}</td>
                    <td style={{ padding: "8px 12px" }}>
                      <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 20, background: inv.piste_audit ? "rgba(26,158,114,0.2)" : "rgba(100,116,139,0.2)", color: inv.piste_audit ? "#1a9e72" : "#4b5563" }}>
                        {inv.piste_audit ? "BC-" + inv.id.toString().padStart(4, "0") : "—"}
                      </span>
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 20, background: inv.piste_audit ? "rgba(26,158,114,0.2)" : "rgba(100,116,139,0.2)", color: inv.piste_audit ? "#1a9e72" : "#4b5563" }}>
                        {inv.piste_audit ? "BL-" + inv.id.toString().padStart(4, "0") : "—"}
                      </span>
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      <span style={{ fontSize: 14 }}>{inv.piste_audit ? "✅" : "⚠️"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
